import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { embeddedLoadTimeoutMs } from '@/constants/embeddedApplication';
import { useEmbeddedAuth } from '@/platform/authentication';
import { embeddedCommunicationService } from '@/services/embeddedCommunicationService';
import { useAppShell } from '@/shell';
import { toEmbeddedShellLanguage } from '@/i18n/languageCodes';
import type {
  EmbeddedApplication,
  EmbeddedNavigationUrlResolutionError,
  EmbeddedNavigationGroup,
  EmbeddedNavigationItem,
  EmbeddedToolbarActionId,
} from '@/types/embeddedApplication';
import { isEmbeddedFeatureVisible } from '@/utils/embeddedApplicationUtils';
import {
  isPortalSelfEmbedUrl,
  resolveEmbeddedNavigationUrl,
} from '@/utils/iframeUrl';
import { launchProductUrlInNewTab } from '@/utils/productNewTabLaunch';

interface UseEmbeddedApplicationOptions {
  application: EmbeddedApplication;
  selectedNavigationItemId?: string;
  userRoles?: string[];
  onNavigationItemChange?: (
    itemId: string,
    item: EmbeddedNavigationItem,
  ) => void;
  onPopOut?: (url: string) => void;
}

function flattenNavigationItems(
  groups: EmbeddedNavigationGroup[],
): EmbeddedNavigationItem[] {
  const collect = (items: EmbeddedNavigationItem[]): EmbeddedNavigationItem[] =>
    items.flatMap((item) => [item, ...collect(item.children ?? [])]);

  return groups.flatMap((group) => collect(group.items));
}

function filterNavigationGroups(
  groups: EmbeddedNavigationGroup[],
  query: string,
  userRoles: string[],
): EmbeddedNavigationGroup[] {
  const normalizedQuery = query.trim().toLowerCase();

  return groups
    .filter(
      (group) =>
        group.visible !== false &&
        isEmbeddedFeatureVisible({ roles: group.permissions }, userRoles),
    )
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        if (item.visible === false) {
          return false;
        }

        if (!isEmbeddedFeatureVisible({ roles: item.permissions }, userRoles)) {
          return false;
        }

        if (!normalizedQuery) {
          return true;
        }

        return item.label.toLowerCase().includes(normalizedQuery);
      }),
    }))
    .filter((group) => group.items.length > 0);
}

function resolveNavigationItemUrl(
  item: EmbeddedNavigationItem | undefined,
): string | undefined {
  return item?.iframeUrl || item?.route;
}

function resolveTrustedOrigin(iframeUrl: string): string | null {
  try {
    return new URL(iframeUrl, window.location.origin).origin;
  } catch {
    return null;
  }
}

export function useEmbeddedApplication({
  application,
  selectedNavigationItemId,
  userRoles = [],
  onNavigationItemChange,
  onPopOut,
}: UseEmbeddedApplicationOptions) {
  const { registerEmbeddedFrame, unregisterEmbeddedFrame } = useEmbeddedAuth();
  const { language } = useAppShell();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const loadTimeoutRef = useRef<number | null>(null);

  const navigationItems = useMemo(
    () => flattenNavigationItems(application.navigation),
    [application.navigation],
  );

  const [internalSelectedItemId, setInternalSelectedItemId] = useState(
    () => selectedNavigationItemId ?? navigationItems[0]?.id ?? '',
  );
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    () =>
      Object.fromEntries(
        application.navigation.map((group) => [
          group.id,
          group.defaultExpanded !== false,
        ]),
      ),
  );
  const [navigationQuery, setNavigationQuery] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isIframeReady, setIsIframeReady] = useState(false);

  const selectedItemId = selectedNavigationItemId ?? internalSelectedItemId;

  const selectedItem = useMemo(
    () => navigationItems.find((item) => item.id === selectedItemId),
    [navigationItems, selectedItemId],
  );

  const filteredNavigation = useMemo(
    () =>
      filterNavigationGroups(
        application.navigation,
        navigationQuery,
        userRoles,
      ),
    [application.navigation, navigationQuery, userRoles],
  );

  const iframeUrlResolution = useMemo(
    () =>
      resolveEmbeddedNavigationUrl({
        applicationBaseUrl: application.iframeUrl,
        navigationTarget: resolveNavigationItemUrl(selectedItem),
      }),
    [application.iframeUrl, selectedItem],
  );

  const resolvedIframeUrl = iframeUrlResolution.url;

  const iframeUrlResolutionError = useMemo<
    EmbeddedNavigationUrlResolutionError | undefined
  >(
    () =>
      iframeUrlResolution.didFallback ? iframeUrlResolution.error : undefined,
    [iframeUrlResolution.didFallback, iframeUrlResolution.error],
  );

  const hasInvalidIframeUrl = useMemo(
    () => !resolvedIframeUrl || isPortalSelfEmbedUrl(resolvedIframeUrl),
    [resolvedIframeUrl],
  );

  const trustedIframeOrigin = useMemo(
    () => (resolvedIframeUrl ? resolveTrustedOrigin(resolvedIframeUrl) : null),
    [resolvedIframeUrl],
  );

  const clearLoadTimeout = useCallback(() => {
    if (loadTimeoutRef.current !== null) {
      window.clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }
  }, []);

  const reloadIframe = useCallback(() => {
    clearLoadTimeout();
    setHasError(false);
    setIsLoading(true);
    setReloadToken((current) => current + 1);

    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.location.reload();
    }
  }, [clearLoadTimeout]);

  const popOut = useCallback(() => {
    if (!resolvedIframeUrl || hasInvalidIframeUrl) {
      return;
    }

    if (onPopOut) {
      onPopOut(resolvedIframeUrl);
      return;
    }

    launchProductUrlInNewTab(resolvedIframeUrl);
  }, [hasInvalidIframeUrl, onPopOut, resolvedIframeUrl]);

  const handleIframeLoad = useCallback(() => {
    clearLoadTimeout();
    setIsLoading(false);
    setHasError(false);
    setIsIframeReady(true);
    const iframeWindow = iframeRef.current?.contentWindow ?? null;

    embeddedCommunicationService.bindIframeWindow(iframeWindow);

    if (!iframeWindow || !trustedIframeOrigin) {
      return;
    }

    registerEmbeddedFrame({
      productId: application.id,
      productName: application.name,
      iframeWindow,
      trustedOrigin: trustedIframeOrigin,
    });

    embeddedCommunicationService.sendToIframe({
      type: 'language',
      payload: {
        language: toEmbeddedShellLanguage(language),
      },
    });
  }, [
    application.id,
    application.name,
    clearLoadTimeout,
    language,
    registerEmbeddedFrame,
    trustedIframeOrigin,
  ]);

  const handleIframeError = useCallback(() => {
    clearLoadTimeout();
    setIsLoading(false);
    setHasError(true);
  }, [clearLoadTimeout]);

  const selectNavigationItem = useCallback(
    (itemId: string) => {
      const item = navigationItems.find((entry) => entry.id === itemId);

      if (!item) {
        return;
      }

      if (selectedNavigationItemId === undefined) {
        setInternalSelectedItemId(itemId);
      }

      setSidebarOpen(false);
      onNavigationItemChange?.(itemId, item);
      embeddedCommunicationService.sendToIframe({
        type: 'navigate',
        payload: {
          itemId: item.id,
          route: item.route || item.iframeUrl,
        },
      });
    },
    [navigationItems, onNavigationItemChange, selectedNavigationItemId],
  );

  const toggleGroup = useCallback((groupId: string) => {
    setExpandedGroups((current) => ({
      ...current,
      [groupId]: !current[groupId],
    }));
  }, []);

  const handleToolbarAction = useCallback(
    (actionId: EmbeddedToolbarActionId) => {
      switch (actionId) {
        case 'reload':
          reloadIframe();
          break;
        case 'popOut':
        case 'openInNewWindow':
          popOut();
          break;
        default:
          break;
      }
    },
    [popOut, reloadIframe],
  );

  useEffect(() => {
    if (selectedNavigationItemId !== undefined) {
      return;
    }

    const selectedStillExists = navigationItems.some(
      (item) => item.id === internalSelectedItemId,
    );

    if (!internalSelectedItemId || !selectedStillExists) {
      setInternalSelectedItemId(navigationItems[0]?.id ?? '');
    }
  }, [internalSelectedItemId, navigationItems, selectedNavigationItemId]);

  useEffect(() => {
    setExpandedGroups(
      Object.fromEntries(
        application.navigation.map((group) => [
          group.id,
          group.defaultExpanded !== false,
        ]),
      ),
    );
  }, [application.id, application.navigation]);

  useEffect(() => {
    setIsLoading(true);
    setHasError(hasInvalidIframeUrl);
    setIsIframeReady(false);
    clearLoadTimeout();

    if (hasInvalidIframeUrl) {
      setIsLoading(false);
      return;
    }

    loadTimeoutRef.current = window.setTimeout(() => {
      setIsLoading(false);
      setHasError(true);
    }, embeddedLoadTimeoutMs);

    return clearLoadTimeout;
  }, [
    application.id,
    clearLoadTimeout,
    hasInvalidIframeUrl,
    reloadToken,
    resolvedIframeUrl,
  ]);

  useEffect(() => {
    if (!trustedIframeOrigin) {
      return;
    }

    let frameId: number | null = null;

    const registerWhenAvailable = () => {
      const iframeWindow = iframeRef.current?.contentWindow ?? null;

      if (!iframeWindow) {
        frameId = window.requestAnimationFrame(registerWhenAvailable);
        return;
      }

      embeddedCommunicationService.bindIframeWindow(iframeWindow);
      registerEmbeddedFrame({
        productId: application.id,
        productName: application.name,
        iframeWindow,
        trustedOrigin: trustedIframeOrigin,
      });
    };

    registerWhenAvailable();

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [
    application.id,
    application.name,
    registerEmbeddedFrame,
    trustedIframeOrigin,
  ]);

  useEffect(() => {
    if (!isIframeReady || !selectedItem) {
      return;
    }

    embeddedCommunicationService.sendToIframe({
      type: 'navigate',
      payload: {
        itemId: selectedItem.id,
        route: selectedItem.route || selectedItem.iframeUrl,
      },
    });
  }, [isIframeReady, selectedItem]);

  useEffect(() => {
    return () => {
      unregisterEmbeddedFrame(application.id);
      embeddedCommunicationService.bindIframeWindow(null);
    };
  }, [application.id, unregisterEmbeddedFrame]);

  const visibleToolbarActions = useMemo(
    () =>
      application.toolbarActions.filter((action) => action.visible !== false),
    [application.toolbarActions],
  );

  return {
    application,
    iframeRef,
    selectedItemId,
    selectedItem,
    filteredNavigation,
    expandedGroups,
    navigationQuery,
    setNavigationQuery,
    sidebarOpen,
    setSidebarOpen,
    toggleGroup,
    selectNavigationItem,
    resolvedIframeUrl,
    iframeUrlResolution,
    iframeUrlResolutionError,
    hasInvalidIframeUrl,
    reloadIframe,
    popOut,
    reloadToken,
    isLoading,
    hasError,
    isIframeReady,
    handleIframeLoad,
    handleIframeError,
    handleToolbarAction,
    visibleToolbarActions,
  };
}

export type UseEmbeddedApplicationResult = ReturnType<
  typeof useEmbeddedApplication
>;
